/**
 * Utility functions for text processing and formatting
 */

/**
 * Convert text with \n characters to bullet points
 * @param {string} text - Text containing \n characters
 * @returns {Array} Array of bullet point strings
 */
export const convertToBulletPoints = (text) => {
  if (!text) return [];
  
  return text
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0);
};

/**
 * Convert text with \n characters to HTML with bullet points
 * @param {string} text - Text containing \n characters
 * @returns {string} HTML string with bullet points
 */
export const convertToHTMLBulletPoints = (text) => {
  if (!text) return '';
  
  const bulletPoints = convertToBulletPoints(text);
  if (bulletPoints.length === 0) return '';
  
  if (bulletPoints.length === 1) {
    return bulletPoints[0];
  }
  
  return `<ul>${bulletPoints.map(point => `<li>${point}</li>`).join('')}</ul>`;
};

/**
 * Check if text contains multiple lines (has \n characters)
 * @param {string} text - Text to check
 * @returns {boolean} True if text has multiple lines
 */
export const hasMultipleLines = (text) => {
  return text && text.includes('\n');
};

/**
 * Format technologies string to array
 * @param {string|Array} technologies - Technologies as string or array
 * @returns {Array} Array of technology strings
 */
export const formatTechnologies = (technologies) => {
  if (!technologies) return [];

  const splitAndClean = (value) => {
    if (!value || typeof value !== 'string') return [];
    return value
      .split(',')
      .map(tech => tech.trim())
      .filter(tech => tech.length > 0);
  };

  // Handle JSON-encoded arrays
  if (typeof technologies === 'string') {
    try {
      const parsed = JSON.parse(technologies);
      if (Array.isArray(parsed)) {
        return formatTechnologies(parsed);
      }
    } catch (e) {
      // Not JSON, continue as comma-separated string
    }
    return splitAndClean(technologies);
  }

  if (Array.isArray(technologies)) {
    const items = technologies.filter(t => typeof t === 'string');
    if (items.length === 0) return [];

    // Some backends/coercions may turn a string into a list of characters.
    // Detect that and re-join into a string first.
    const looksLikeCharArray = items.length > 15 && items.every(s => s.length <= 2);
    if (looksLikeCharArray) {
      return formatTechnologies(items.join(''));
    }

    const flattened = items.flatMap(item => splitAndClean(item));

    // De-duplicate (case-insensitive) while preserving order
    const seen = new Set();
    return flattened.filter(tech => {
      const key = tech.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  return [];
};

/**
 * Format achievements string to array - splits by newlines, handles arrays with embedded newlines
 * @param {string|Array} achievements - Achievements as string or array
 * @returns {Array} Array of achievement strings
 */
export const formatAchievements = (achievements) => {
  if (!achievements) return [];

  if (Array.isArray(achievements)) {
    // If it's an array, check each element for embedded newlines
    const result = [];
    achievements.forEach(achievement => {
      if (typeof achievement === 'string' && achievement.includes('\n')) {
        // Split this achievement by newlines
        const subAchievements = achievement
          .split('\n')
          .map(line => line.trim())
          .filter(line => line.length > 0);
        result.push(...subAchievements);
      } else if (achievement && achievement.trim()) {
        result.push(achievement.trim());
      }
    });
    return result;
  }

  if (typeof achievements === 'string') {
    // Try to parse as JSON first
    try {
      const parsed = JSON.parse(achievements);
      if (Array.isArray(parsed)) {
        return formatAchievements(parsed); // Recursively process the parsed array
      }
    } catch (e) {
      // Not JSON, split ONLY by newlines (not commas)
      return achievements
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);
    }
  }

  return [];
};

/**
 * Truncate text to specified length with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 150) => {
  if (!text || text.length <= maxLength) return text;
  
  return text.substring(0, maxLength).trim() + '...';
};

/**
 * Format date range for display
 * @param {string} startDate - Start date
 * @param {string} endDate - End date (optional)
 * @returns {string} Formatted date range
 */
export const formatDateRange = (startDate, endDate) => {
  if (!startDate) return '';
  
  const end = endDate || 'Present';
  return `${startDate} - ${end}`;
};

/**
 * Generate initials from company/organization name
 * @param {string} name - Company or organization name
 * @returns {string} Initials (max 2 characters)
 */
export const generateInitials = (name) => {
  if (!name) return '?';
  
  const words = name.split(' ').filter(word => word.length > 0);
  
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  
  return words
    .slice(0, 2)
    .map(word => word.charAt(0).toUpperCase())
    .join('');
};
